import { useState } from "react";
import whatsappIcon from "../assets/icons/whatsapp.svg";
import NavBar from "../components/navigation";
import Footer from "../components/Footer";

// Update this to your real breakdown hotline.
const HOTLINE = {
  phone: "+254 703 398787",
  whatsapp: "254703398787",
};

// ── Icons ──────────────────────────────────────────────────────

function PhoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.5 3h3l2 5-2.5 1.5a12 12 0 0 0 5.5 5.5L16 12.5l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 4.5 5a2 2 0 0 1 2-2z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function ChatIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-4.5 7.5 8.5 8.5 0 0 1-8.9-.4L3 21l1.9-4.6a8.38 8.38 0 0 1-.9-3.9 8.5 8.5 0 0 1 8.5-8.5h.5a8.48 8.48 0 0 1 8 8v.5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3.5 2 20h20L12 3.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="17" r="0.9" fill="currentColor" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Helpers ────────────────────────────────────────────────────

function buildWhatsappMessage(coords, note, landmark) {
  const lines = ["My car has broken down and I need assistance."];

  if (coords) {
    lines.push(`My location: https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`);
  } else {
    lines.push("I couldn't share my GPS location — please call me for directions.");
  }

  if (landmark.trim()) {
    lines.push(`Nearby landmark: ${landmark.trim()}`);
  } else {
    lines.push("Am I near a landmark? Please ask me for one if needed.");
  }

  if (note.trim()) {
    lines.push(`Details: ${note.trim()}`);
  }

  return lines.join("\n");
}

const LOCATION_STATUS_CONTENT = {
  located: { icon: <CheckIcon />, text: "Location shared — help can find you.", className: "text-green-700" },
  "location-failed": { icon: <WarnIcon />, text: "Location unavailable — you can still call or message us.", className: "text-amber-700" },
};

function LocationStatus({ status }) {
  const content = LOCATION_STATUS_CONTENT[status];
  if (!content) return null;
  return (
    <p className={`flex items-center justify-center gap-2 text-sm font-medium ${content.className}`}>
      {content.icon}
      {content.text}
    </p>
  );
}

// ── Flow ───────────────────────────────────────────────────────

function BreakdownFlow({ status, onStart, note, onNoteChange, landmark, onLandmarkChange, whatsappHref }) {
  if (status === "idle") {
    return (
      <button
        type="button"
        onClick={onStart}
        className="themed mt-10 flex w-full items-center justify-center gap-2 rounded-xl px-6 py-4 text-base font-bold text-white shadow-lg shadow-[#c1392a]/20 transition-all hover:bg-[#a62f24] hover:shadow-[#c1392a]/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c1392a]"
      >
        <PhoneIcon />
        Call for Breakdown Assistance
      </button>
    );
  }

  if (status === "locating") {
    return (
      <div className="mt-10 flex flex-col items-center gap-3 text-gray-500">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-[#c1392a]" aria-hidden="true" />
        <span className="text-muted">Getting your location...</span>
      </div>
    );
  }

  return (
    <div className="mt-10 space-y-4">
      <LocationStatus status={status} />

      <textarea
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder="What's wrong? (optional — e.g. flat tyre, won't start)"
        rows={2}
        aria-label="Describe the issue (optional)"
        className="w-full resize-none rounded-xl border border-border bg-white px-4 py-3 text-sm text-dark placeholder:text-muted focus:outline-none focus-visible:border-[#c1392a] focus-visible:ring-2 focus-visible:ring-[#c1392a]/20"
      />

      <textarea
        value={landmark}
        onChange={(e) => onLandmarkChange(e.target.value)}
        placeholder="Are you near a landmark? (optional — e.g. TotalEnergies Westlands)"
        rows={2}
        aria-label="Nearby landmark (optional)"
        className="w-full resize-none rounded-xl border border-border bg-white px-4 py-3 text-sm text-dark placeholder:text-muted focus:outline-none focus-visible:border-[#c1392a] focus-visible:ring-2 focus-visible:ring-[#c1392a]/20"
      />

      <div className="flex flex-col gap-3 sm:flex-row">
        <a
          href={`tel:${HOTLINE.phone}`}
          className="themed flex flex-1 items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base font-bold text-white shadow-md shadow-[#c1392a]/20 transition-all hover:bg-[#a62f24] hover:shadow-[#c1392a]/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c1392a]"
        >
          <PhoneIcon />
          Call Now
        </a>
        <a
          href={whatsappHref}
          className="bg-green-600 flex flex-1 items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base font-bold text-white shadow-md shadow-[#c1392a]/20 transition-all hover:bg-[#a62f24] hover:shadow-[#c1392a]/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c1392a]"
        >
          <img src={whatsappIcon} alt="" className="h-5 w-5 wp-btn" aria-hidden="true"  />
          Send via WhatsApp
        </a>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────

export default function Breakdown() {
  const [status, setStatus] = useState("idle"); // idle | locating | located | location-failed
  const [coords, setCoords] = useState(null);
  const [note, setNote] = useState("");
  const [landmark, setLandmark] = useState("");

  const requestHelp = () => {
    setStatus("locating");

    if (!navigator.geolocation) {
      setStatus("location-failed");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        setStatus("located");
      },
      () => setStatus("location-failed"),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const whatsappHref = `https://wa.me/${HOTLINE.whatsapp}?text=${encodeURIComponent(buildWhatsappMessage(coords, note, landmark))}`;

  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-background px-4 py-16 sm:px-6 lg:py-24">
      <main className="mx-auto max-w-2xl">
        <section className="glass-card rounded-3xl border-border px-6 py-10 text-center shadow-xl shadow-dark/5 sm:px-12 sm:py-14">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#c1392a]/10 text-[#c1392a]">
          <WarnIcon />
        </div>
        <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-[#c1392a]">Roadside assistance</p>
        <h1 className="mt-3 font-heading text-3xl font-bold text-dark sm:text-4xl">Car Broken Down?</h1>
        <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-muted">
          Tap below to share your live location and get help sent straight to where you are.
        </p>

        <div aria-live="polite">
          <BreakdownFlow
            status={status}
            onStart={requestHelp}
            note={note}
            onNoteChange={setNote}
            landmark={landmark}
            onLandmarkChange={setLandmark}
            whatsappHref={whatsappHref}
          />
        </div>
        </section>
      </main>
    </div>
    <Footer />
    </>
  );
}